/**
 * Recovery from MSAL's stuck `interaction_in_progress` flag.
 *
 * THE BUG THIS EXISTS FOR. MSAL sets an interaction-status flag in
 * sessionStorage the moment an interactive call starts, and clears it when
 * handleRedirectPromise() processes the response. If the user never comes back
 * — closes the tab on the Microsoft page, or abandons an "Approval required"
 * consent screen — no response is ever processed, so the flag survives. Every
 * later interactive call then throws
 * `BrowserAuthError: interaction_in_progress`, and sign-in is dead until the
 * user clears site data by hand. Telling a user to clear storage is not a
 * recovery story.
 *
 * The app's MSAL init is NOT the problem here, which is worth recording because
 * it is the usual suspect: AppComponent awaits initialize() then
 * handleRedirectPromise() before router.initialNavigation(), and
 * MsalRedirectComponent is bootstrapped. The gap was that nothing guarded the
 * interactive calls themselves and nothing recognised the error.
 *
 * WHY THE KEY IS DISCOVERED RATHER THAN CONSTRUCTED. The flag's name has moved
 * between MSAL majors — `msal.interaction.status` in v2, and
 * `msal.<clientId>.interaction.status` in v3/v4 — and MSAL exposes no public API
 * to clear it. Matching on the suffix works across both and cannot be broken by
 * a clientId change, which a hand-built key would be.
 */

/** Sessions store the flag here; localStorage is never used for it. */
const INTERACTION_STATUS_SUFFIX = 'interaction.status';

/** True for the specific MSAL error this module recovers from. */
export function isInteractionInProgress(error: unknown): boolean {
  const code =
    (error as { errorCode?: string })?.errorCode ??
    (error as { message?: string })?.message ??
    '';
  return String(code).includes('interaction_in_progress');
}

/**
 * Every interaction-status key currently present, whatever MSAL major wrote it.
 *
 * Returns names only; the value is a status string, never a credential.
 */
export function interactionLockKeys(): string[] {
  const found: string[] = [];
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.endsWith(INTERACTION_STATUS_SUFFIX)) {
        found.push(key);
      }
    }
  } catch {
    // Storage blocked (private mode). Nothing to clear, and nothing is stuck
    // either — MSAL could not have written the flag in the first place.
  }
  return found;
}

/** Whether an interaction is currently marked in progress. */
export function isInteractionLocked(): boolean {
  return interactionLockKeys().length > 0;
}

/**
 * Drop the flag so the next interactive call can start.
 *
 * Returns the keys removed, so a caller can say what it did rather than
 * claiming success it did not verify.
 */
export function clearInteractionLock(): string[] {
  const keys = interactionLockKeys();
  for (const key of keys) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // best effort
    }
  }
  return keys;
}

/**
 * Run an interactive MSAL call, recovering ONCE from a stuck flag.
 *
 * Deliberately bounded at one retry. If the flag is genuinely set because an
 * interaction really is in flight, clearing it and trying again would start a
 * second interaction on top of the first; one attempt converts the dead end into
 * a working sign-in, while a loop would trade it for a different bug.
 *
 * Returns true if the call was started.
 */
export function startInteraction(attempt: () => unknown, what: string): boolean {
  const handleFailure = (err: unknown, isRetry: boolean): boolean => {
    if (!isInteractionInProgress(err)) {
      console.warn(`MSAL interactive call failed to start (${what})`, err);
      return false;
    }
    if (isRetry) {
      console.warn(
        `MSAL interactive call still refused after clearing the stale flag ` +
          `(${what}). Signing in again from a fresh tab is the remaining option.`,
        err
      );
      return false;
    }
    const cleared = clearInteractionLock();
    console.warn(
      `MSAL reported interaction_in_progress for ${what} with no interaction ` +
        `actually running — a sign-in or consent screen was abandoned, or the ` +
        `flag was left by a DIFFERENT clientId, which MSAL cannot clear itself. ` +
        `Cleared ${cleared.length} stale flag(s) and retrying once.`
    );
    return run(true);
  };

  // THE FAILURE CAN ARRIVE THREE WAYS, and it took two wrong fixes to learn it.
  //
  //   1. a synchronous throw
  //   2. a REJECTED PROMISE — msal-browser's instance.acquireTokenRedirect()
  //   3. an OBSERVABLE that errors — msal-angular's MsalService.loginRedirect(),
  //      which is `from(this.instance.loginRedirect(request))`
  //
  // Attempt one used a bare try/catch: it could not see (2) or (3), and its spec
  // threw synchronously, so code and test agreed with each other while both
  // disagreed with MSAL. Attempt two added thenable handling, which still could
  // not see (3) — an Observable has no .then — and the deployed build dead-ended
  // identically. The full stack is what settled it: loginRedirect calls
  // acquireTokenRedirect internally, and msal-angular hands back an Observable.
  //
  // Case (3) also exposes a defect that has nothing to do with the stuck flag:
  // `from()` wraps a promise that MSAL has ALREADY created, so the redirect fires
  // whether or not anyone subscribes — which is why an unsubscribed
  // loginRedirect worked in the happy path and lost every error. Subscribing
  // here is what makes those errors reachable at all.
  const run = (isRetry: boolean): boolean => {
    try {
      const result = attempt() as {
        then?: (ok?: unknown, err?: (e: unknown) => unknown) => unknown;
        subscribe?: (observer: { error: (e: unknown) => void }) => unknown;
      } | null;

      if (result && typeof result.subscribe === 'function') {
        result.subscribe({ error: (err: unknown) => void handleFailure(err, isRetry) });
      } else if (result && typeof result.then === 'function') {
        result.then(undefined, (err: unknown) => handleFailure(err, isRetry));
      }
      return true;
    } catch (err) {
      return handleFailure(err, isRetry);
    }
  };

  return run(false);
}

/**
 * Clear a flag left over from an interaction that never returned.
 *
 * Call this ONLY once handleRedirectPromise() has resolved with no response and
 * the URL carries no auth fragment: together those mean nothing is in flight, so
 * a flag still sitting there belongs to an abandoned attempt. Calling it any
 * earlier could cancel a redirect that is genuinely mid-flight.
 */
export function clearStaleInteractionLock(hadRedirectResponse: boolean, hash: string): string[] {
  if (hadRedirectResponse) return [];
  if (/(^|[#&])(code|error|state)=/.test(hash || '')) return [];
  const cleared = clearInteractionLock();
  if (cleared.length) {
    console.warn(
      `Cleared ${cleared.length} stale MSAL interaction flag(s) at startup: no ` +
        `redirect response and no auth fragment, so the interaction that set ` +
        `them was abandoned. Without this the next sign-in would throw ` +
        `interaction_in_progress.`
    );
  }
  return cleared;
}
